"use client";

import { useState, useRef, useCallback } from "react";
import { Info, Upload, FileText, X, CheckCircle, Loader2 } from "lucide-react";
import { useUploadNdisCertificateMutation } from "@/redux/features/divisions/divisionsApi";

// Types
export type FundingType = "medicare" | "ndis" | "not-applicable";
export type DocumentUploadMethod = "upload" | "bring-later";

export interface FundingSupportData {
  fundingType: FundingType | null;
  certificateNumber: string;
  documentMethod: DocumentUploadMethod | null;
  uploadedFile: File | null;
  isUploaded?: boolean; // Track if file was uploaded to server
}

interface FundingSupportProps {
  title?: string;
  subtitle?: string;
  onDataChange?: (data: FundingSupportData) => void;
  defaultData?: Partial<FundingSupportData>;
  // Optional: If appointmentId is provided, upload file immediately after selection
  appointmentId?: string;
  onUploadComplete?: (success: boolean) => void;
}

// Funding option configuration
const fundingOptions: {
  id: FundingType;
  name: string;
  description: string;
}[] = [
  {
    id: "medicare",
    name: "Medicare eligible",
    description:
      "Use your Medicare rebate if you have an eligible referral or care plan.",
  },
  {
    id: "ndis",
    name: "NDIS program eligible",
    description:
      "Use your NDIS funding if you have an approved plan that covers this service.",
  },
  {
    id: "not-applicable",
    name: "Not Applicable",
    description:
      "I will pay the full fee without Medicare rebate or NDIS funding.",
  },
];

// Funding Option Card Component
const FundingOptionCard = ({
  option,
  isSelected,
  onSelect,
}: {
  option: (typeof fundingOptions)[0];
  isSelected: boolean;
  onSelect: () => void;
}) => {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      aria-label={`Select ${option.name}`}
      className={`
        group flex w-full flex-col items-start overflow-hidden rounded-[10px] border p-5
        text-left transition-all duration-200 ease-out
        focus:outline-none focus-visible:ring-2 focus-visible:ring-red focus-visible:ring-offset-2
        active:scale-[0.99]
        ${
          isSelected
            ? "border-red bg-red/15 shadow-[0px_0px_0px_7px_rgba(192,22,16,0.4)]"
            : "border-red bg-transparent hover:bg-red/5"
        }
      `}
    >
      <div className="flex w-full flex-col pt-1">
        <div className="p-2.5">
          <span className="font-onest text-xl font-bold leading-none text-black md:text-2xl">
            {option.name}
          </span>
        </div>
        <div className="p-2.5">
          <span className="font-onest text-sm font-normal leading-tight text-black md:text-base">
            {option.description}
          </span>
        </div>
      </div>
    </button>
  );
};

// Selected Info Card Component
const SelectedInfoCard = ({
  fundingType,
}: {
  fundingType: FundingType;
}) => {
  const option = fundingOptions.find((o) => o.id === fundingType);
  if (!option) return null;

  return (
    <div className="flex w-full items-center gap-2.5 overflow-hidden rounded-[10px] border border-red bg-red/15 p-5">
      <div className="flex size-[49.5px] shrink-0 items-center justify-center rounded-full bg-white p-3">
        <Info className="size-6 text-red" aria-hidden="true" />
      </div>
      <div className="flex flex-1 flex-col">
        <div className="px-2.5 py-1.5">
          <span className="font-onest text-xl font-bold leading-none text-black md:text-2xl">
            {option.name}
          </span>
        </div>
        <div className="px-2.5 py-1.5">
          <span className="font-onest text-sm font-normal leading-tight text-black md:text-base">
            {option.description}
          </span>
        </div>
      </div>
    </div>
  );
};

// Document Upload Component
const DocumentUpload = ({
  onFileSelect,
  selectedFile,
  onRemoveFile,
  isUploading,
  isUploaded,
}: {
  onFileSelect: (file: File) => void;
  selectedFile: File | null;
  onRemoveFile: () => void;
  isUploading?: boolean;
  isUploaded?: boolean;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file && isValidFile(file)) {
        onFileSelect(file);
      }
    },
    [onFileSelect]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && isValidFile(file)) {
      onFileSelect(file);
    }
  };

  const isValidFile = (file: File) => {
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    const maxSize = 5 * 1024 * 1024; // 5MB
    return validTypes.includes(file.type) && file.size <= maxSize;
  };

  if (selectedFile) {
    return (
      <div className="flex w-full flex-col items-center gap-2 rounded-[10px] border border-red border-dashed p-10">
        <div className="flex items-center gap-3">
          <FileText className="size-8 text-red" />
          <span className="font-onest text-sm font-medium text-black">
            {selectedFile.name}
          </span>
          {!isUploading && (
            <button
              type="button"
              onClick={onRemoveFile}
              className="rounded-full p-1 hover:bg-red/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-red"
              aria-label="Remove file"
            >
              <X className="size-5 text-red" />
            </button>
          )}
        </div>
        {isUploading ? (
          <div className="flex items-center gap-2 text-red">
            <Loader2 className="size-5 animate-spin" />
            <span className="font-onest text-sm">Uploading...</span>
          </div>
        ) : isUploaded ? (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="size-5" />
            <span className="font-onest text-sm">File uploaded to server</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="size-5" />
            <span className="font-onest text-sm">File ready for upload</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => fileInputRef.current?.click()}
      onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`
        flex w-full cursor-pointer flex-col items-center gap-2.5 rounded-[10px] border border-dashed p-10
        transition-colors duration-200
        focus:outline-none focus-visible:ring-2 focus-visible:ring-red focus-visible:ring-offset-2
        ${isDragging ? "border-red bg-red/10" : "border-red hover:bg-red/5"}
      `}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf"
        onChange={handleFileChange}
        className="hidden"
        aria-label="Upload document"
      />
      <Upload className="size-8 text-red" aria-hidden="true" />
      <p className="font-onest text-sm font-medium text-red">
        Choose file or drag and drop here
      </p>
      <p className="font-onest text-xs text-gray-500">
        JPG, JPEG, PNG, and PDF formats up to 5MB
      </p>
    </div>
  );
};

// Bring Later Option Component
const BringLaterOption = ({
  isSelected,
  onSelect,
}: {
  isSelected: boolean;
  onSelect: () => void;
}) => {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      className={`
        flex h-[165px] w-full flex-col items-center justify-center rounded-[10px] border border-dashed p-10
        transition-all duration-200
        focus:outline-none focus-visible:ring-2 focus-visible:ring-red focus-visible:ring-offset-2
        ${
          isSelected
            ? "border-red bg-red/15 shadow-[0px_0px_0px_7px_rgba(192,22,16,0.4)]"
            : "border-red hover:bg-red/5"
        }
      `}
    >
      <p className="text-center font-onest text-sm font-medium text-red">
        Bring the document in
        <br />
        during the first session.
      </p>
    </button>
  );
};

// Main FundingSupport Component
const FundingSupport = ({
  title = "Do You Have Funding or Rebate Support?",
  subtitle = "Let us know if you are eligible for NDIS funding or a Medicare rebate for this service.",
  onDataChange,
  defaultData,
  appointmentId,
  onUploadComplete,
}: FundingSupportProps) => {
  const [fundingType, setFundingType] = useState<FundingType | null>(
    defaultData?.fundingType || null
  );
  const [certificateNumber, setCertificateNumber] = useState(
    defaultData?.certificateNumber || ""
  );
  const [documentMethod, setDocumentMethod] = useState<DocumentUploadMethod | null>(
    defaultData?.documentMethod || null
  );
  const [uploadedFile, setUploadedFile] = useState<File | null>(
    defaultData?.uploadedFile || null
  );
  const [isUploaded, setIsUploaded] = useState(defaultData?.isUploaded || false);

  // NDIS certificate upload mutation
  const [uploadCertificate, { isLoading: isUploading }] =
    useUploadNdisCertificateMutation();

  const updateData = (updates: Partial<FundingSupportData>) => {
    const newData: FundingSupportData = {
      fundingType: updates.fundingType ?? fundingType,
      certificateNumber: updates.certificateNumber ?? certificateNumber,
      documentMethod: updates.documentMethod ?? documentMethod,
      uploadedFile: updates.uploadedFile ?? uploadedFile,
      isUploaded: updates.isUploaded ?? isUploaded,
    };
    onDataChange?.(newData);
  };

  const handleFundingTypeSelect = (type: FundingType) => {
    setFundingType(type);
    // Reset document fields when changing funding type
    if (type === "not-applicable") {
      setCertificateNumber("");
      setDocumentMethod(null);
      setUploadedFile(null);
      setIsUploaded(false);
      updateData({
        fundingType: type,
        certificateNumber: "",
        documentMethod: null,
        uploadedFile: null,
        isUploaded: false,
      });
    } else {
      updateData({ fundingType: type });
    }
  };

  const handleCertificateChange = (value: string) => {
    setCertificateNumber(value);
    updateData({ certificateNumber: value });
  };

  const handleFileSelect = async (file: File) => {
    setUploadedFile(file);
    setDocumentMethod("upload");
    setIsUploaded(false);

    // If appointmentId is provided, upload immediately
    if (appointmentId && fundingType === "ndis") {
      try {
        await uploadCertificate({
          appointmentId,
          file,
        }).unwrap();
        setIsUploaded(true);
        updateData({
          uploadedFile: file,
          documentMethod: "upload",
          isUploaded: true,
        });
        onUploadComplete?.(true);
      } catch (error) {
        console.error("Failed to upload NDIS certificate:", error);
        updateData({ uploadedFile: file, documentMethod: "upload", isUploaded: false });
        onUploadComplete?.(false);
      }
    } else {
      updateData({ uploadedFile: file, documentMethod: "upload" });
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setDocumentMethod(null);
    setIsUploaded(false);
    updateData({ uploadedFile: null, documentMethod: null, isUploaded: false });
  };

  const handleBringLater = () => {
    setDocumentMethod("bring-later");
    setUploadedFile(null);
    setIsUploaded(false);
    updateData({
      documentMethod: "bring-later",
      uploadedFile: null,
      isUploaded: false,
    });
  };

  const showDocumentSection = fundingType === "medicare" || fundingType === "ndis";

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex flex-col">
        <div className="p-2.5">
          <h2 className="font-crimsonText text-2xl font-bold leading-tight text-[#500300] sm:text-3xl md:text-[42px] md:leading-none">
            {title}
          </h2>
        </div>
        <div className="w-full max-w-[565px] p-2.5">
          <p className="font-onest text-base font-normal leading-[1.1] text-[#500300] md:text-lg">
            {subtitle}
          </p>
        </div>
      </div>

      {/* Funding Options Grid */}
      <div className="p-[30px]">
        <div
          className="grid grid-cols-1 gap-6 md:grid-cols-2"
          role="radiogroup"
          aria-label="Funding support options"
        >
          <div className="flex flex-col gap-6">
            <FundingOptionCard
              option={fundingOptions[0]}
              isSelected={fundingType === "medicare"}
              onSelect={() => handleFundingTypeSelect("medicare")}
            />
            <FundingOptionCard
              option={fundingOptions[2]}
              isSelected={fundingType === "not-applicable"}
              onSelect={() => handleFundingTypeSelect("not-applicable")}
            />
          </div>
          <div>
            <FundingOptionCard
              option={fundingOptions[1]}
              isSelected={fundingType === "ndis"}
              onSelect={() => handleFundingTypeSelect("ndis")}
            />
          </div>
        </div>
      </div>

      {/* Selected Info Card */}
      {fundingType && (
        <div className="p-[30px]">
          <SelectedInfoCard fundingType={fundingType} />
        </div>
      )}

      {/* Supporting Document Details - Only show for Medicare/NDIS */}
      {showDocumentSection && (
        <>
          {/* Certificate Information Section */}
          <div className="flex flex-col gap-5 p-[30px]">
            <div className="flex flex-col">
              <div className="p-2.5">
                <h3 className="font-crimsonText text-2xl font-bold leading-tight text-[#500300] sm:text-3xl md:text-[42px] md:leading-none">
                  Supporting Document Details
                </h3>
              </div>
              <div className="w-full max-w-[565px] p-2.5">
                <p className="font-onest text-base font-normal leading-[1.1] text-[#500300] md:text-lg">
                  Document Information
                </p>
              </div>
            </div>

            {/* Certificate Input Card */}
            <div className="flex flex-col gap-2.5 rounded-[10px] border border-red p-10">
              <div className="flex flex-col">
                <div className="p-2.5">
                  <span className="font-onest text-lg font-bold leading-[1.1] text-[#500300]">
                    Certificate Information
                  </span>
                </div>
                <div className="p-2.5">
                  <span className="font-onest text-base font-normal leading-[1.1] text-[#500300]">
                    Please enter your{" "}
                    {fundingType === "ndis"
                      ? "NDIS Participant Number"
                      : "Medicare Card Number"}{" "}
                    to confirm your funding or rebate details.
                  </span>
                </div>
              </div>
              <div className="rounded-[10px] border border-red/50 p-2.5">
                <input
                  type="text"
                  value={certificateNumber}
                  onChange={(e) => handleCertificateChange(e.target.value)}
                  placeholder={
                    fundingType === "ndis"
                      ? "Enter NDIS Participant Number"
                      : "Enter Medicare Card Number"
                  }
                  className="w-full bg-transparent p-2.5 font-onest text-base font-normal text-black placeholder:text-black/40 focus:outline-none"
                  aria-label={
                    fundingType === "ndis"
                      ? "NDIS Participant Number"
                      : "Medicare Card Number"
                  }
                />
              </div>
            </div>
          </div>

          {/* Document Upload Section */}
          <div className="flex flex-col gap-5 p-[30px]">
            <div className="flex flex-col">
              <div className="p-2.5">
                <h3 className="font-crimsonText text-2xl font-bold leading-tight text-[#500300] sm:text-3xl md:text-[42px] md:leading-none">
                  Supporting Document Details
                </h3>
              </div>
              <div className="w-full max-w-[565px] p-2.5">
                <p className="font-onest text-base font-normal leading-[1.1] text-[#500300] md:text-lg">
                  Document Information
                </p>
              </div>
            </div>

            {/* Upload Options */}
            <div className="grid grid-cols-1 gap-6 p-[30px] md:grid-cols-2">
              <DocumentUpload
                onFileSelect={handleFileSelect}
                selectedFile={uploadedFile}
                onRemoveFile={handleRemoveFile}
                isUploading={isUploading}
                isUploaded={isUploaded}
              />
              <BringLaterOption
                isSelected={documentMethod === "bring-later"}
                onSelect={handleBringLater}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FundingSupport;
